import React, { useState } from 'react';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import {
  ThemeProvider,
  DialogContentText,
  useMediaQuery,
  IconButton,
} from '@material-ui/core';
import { neutralTheme } from 'appDir/lib/muiTheme';
import { useMutation, gql } from '@apollo/client';
import {
  toastServerError,
  toastValidationErrorsApollo,
} from 'appDir/lib/functions/toastError';
import { textFieldErrorPropsApollo } from 'appDir/lib/functions/materialUIHelpers';
import { CircularLoading } from 'appDir/lib/components/Loading';
import CloseIcon from '@material-ui/icons/Close';

const CREATE_ANNOUNCEMENT = gql`
  mutation CreateAnnouncement($input: CreateAnnouncementInput!) {
    createAnnouncement(input: $input) {
      announcement {
        id
        slug
      }
      errors {
        message
        path
      }
    }
  }
`;

interface Props {
  open: boolean;
  onClose: () => void;
}

const NewAnnouncementDialog = ({ open, onClose }: Props) => {
  const [isPublic, setIsPublic] = useState(true);
  const [title, setTitle] = React.useState('');
  const [
    createAnnouncement,
    { loading, data: createAnnouncementData },
  ] = useMutation(CREATE_ANNOUNCEMENT, {
    onError: toastServerError,
    onCompleted: ({ createAnnouncement: { announcement, errors } }) => {
      if (announcement) {
        onClose();
        window.location.href = `/announcements/${announcement.id}/${announcement.slug}?ep_mode=edit`;
      } else {
        toastValidationErrorsApollo(errors, ['title']);
      }
    },
  });
  const isMobile = useMediaQuery('(max-width:600px)');
  // HOOKS END

  const handleSubmit = (e: React.FormEvent | React.MouseEvent) => {
    e.preventDefault();
    createAnnouncement({ variables: { input: { title, public: isPublic } } });
  };

  const handleExited = () => {
    setTitle('');
    setIsPublic(true);
  };

  return (
    <ThemeProvider theme={neutralTheme}>
      <Dialog
        open={open}
        onClose={onClose}
        onExited={handleExited}
        fullScreen={isMobile}
      >
        <div style={{ position: 'absolute', top: 0, right: 0 }}>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </div>
        <DialogTitle>New Announcement</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Announcements are a way to post and send updates to members of your
            organization. Type the title below, and on the next screen you can
            add details and send the Announcement via text or email to groups.
          </DialogContentText>
          <form onSubmit={handleSubmit}>
            <TextField
              value={title}
              autoFocus
              className="material-ui"
              onChange={e => {
                setTitle(e.target.value);
              }}
              fullWidth
              label="Title"
              {...textFieldErrorPropsApollo(createAnnouncementData, 'title')}
            />
          </form>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            variant="contained"
            disabled={!Boolean(title) || loading}
          >
            Next...
          </Button>
        </DialogActions>
      </Dialog>
      {loading && <CircularLoading />}
    </ThemeProvider>
  );
};

export default NewAnnouncementDialog;
